/**
 * Salesforce as a user-import source.
 *
 * The admin picks an SF object + a saved List View (same UX shape as picking
 * a Google Sheets tab — the list view is the "container" that pins both the
 * row filter and the column set). Field-mapping uses the shared
 * CustomImportMultiMappingForm2; column names come from the list view's
 * describe (SF-21).
 */

import { Doc } from '@dadohr/shared/domainTypes/document';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import {
  UserImportDataSourceSalesforce,
  UserImportDataSourceStatus,
  UserImportMapping,
  UserImportSourceType
} from '@dadohr/shared/domainTypes/user';
import { FS } from '@dadohr/shared/versions';
import {
  Button,
  DialogActions,
  DialogContent,
  MenuItem,
  TextField,
  Typography
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Users } from 'lucide-react';
import { useState } from 'react';
import shortid from 'shortid';
import { AlertBox } from '../../../../../../components/AlertBox';
import { ButtonWithPromise } from '../../../../../../components/ButtonWithPromise';
import { CustomInputLabel } from '../../../../../../components/CustomInputLabel';
import { DeleteButton } from '../../../../../../components/DeletionConfirmation';
import { DialogActionsWithSlots } from '../../../../../../components/DialogActionsWithSlots';
import {
  FormRow,
  FormSection
} from '../../../../../../components/Form/FormRow';
import { Loader } from '../../../../../../components/Loader';
import { SomethingWentWrong } from '../../../../../../components/SomethingWentWrong';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { removeDoc, setDoc } from '../../../../../../services/firebase';
import { now } from '../../../../../../services/time';
import { useUserFields } from '../../../../../../services/user';
import {
  SalesforceListViewSummary,
  useSalesforceConfig,
  useSalesforceListViewColumns,
  useSalesforceListViews,
  useSalesforceObjects
} from '../../../../../Org/pages/Integrations/integrations/salesforce/service';
import { CustomImportMultiMappingForm2 } from '../CustomImportMappingForm';
import { Img, ImportSourceListItem, IntegrationNotConfigured } from './common';
import { IMPORT_META } from './meta';

const META = IMPORT_META.SALESFORCE;

// Default to Contact — every SF org has one and that's the canonical
// "person" object credentialing / HR customers will pull from.
const DEFAULT_OBJECT = 'Contact';

const emptyMapping = (): UserImportMapping => ({ dadoKey: '', otherKey: '' });

// ---------------------------------------------------------------------------
// Existing setup — view + edit a saved SF import source
// ---------------------------------------------------------------------------

export const SalesforceExistingSetup = ({
  d,
  onClose,
  customFields
}: {
  d: Doc<UserImportDataSourceSalesforce>;
  onClose: () => void;
  customFields: CustomUserField[];
}) => {
  const { t } = useUnsafeTranslation();
  const userFields = useUserFields(customFields);

  const [state, setState] = useState(d);

  const isDataChanged = () =>
    JSON.stringify(d.data.customMappings) !== JSON.stringify(state.data.customMappings) ||
    JSON.stringify(d.data.inferredOtherUsers ?? []) !==
      JSON.stringify(state.data.inferredOtherUsers ?? []);

  return (
    <>
      <DialogContent>
        <FormRow>
          <Img maxHeight={32} src={META.logoUrl} />
        </FormRow>
        <FormRow>
          <Alert severity="success" icon={<Users />}>
            {t(
              "This Salesforce list view is currently set up as a user import source. New or updated rows in the list view will be imported to Dado."
            )}
          </Alert>
        </FormRow>
        <FormRow>
          <CustomInputLabel label={t('Object')}>
            <div>{d.data.objectName}</div>
          </CustomInputLabel>
          <CustomInputLabel label={t('List view')}>
            <div>{d.data.listViewName}</div>
          </CustomInputLabel>
        </FormRow>
        <CustomImportMultiMappingForm2
          additionalCompositeFields={['$FULL_NAME$']}
          enableSave={isDataChanged()}
          fieldTerm={t('column')}
          integrationName="Salesforce"
          userFields={userFields}
          value={{
            main: state.data.customMappings,
            others: state.data.inferredOtherUsers?.map((v) => v.customMappings)
          }}
          onChange={(nextVal) =>
            setState((x) => ({
              ...x,
              data: {
                ...x.data,
                customMappings: nextVal.main,
                inferredOtherUsers:
                  nextVal.others?.map((v) => ({ customMappings: v })) || []
              }
            }))
          }
        />
      </DialogContent>
      <DialogActionsWithSlots
        left={
          <DeleteButton
            variant="contained"
            color="secondary"
            onDelete={() => removeDoc(d).then(onClose)}
            keyword={d.id}
            dialogTitle={t('Remove integration') + '?'}
            buttonAction={t('Remove integration')}
          >
            {t('Remove integration')}
          </DeleteButton>
        }
        right={
          <>
            <Button onClick={onClose}>{t('Close')}</Button>
            <ButtonWithPromise
              color="primary"
              variant="contained"
              disabled={!isDataChanged()}
              pending={`${t('Saving')}...`}
              onClick={() => setDoc(state)}
            >
              {t('Save')}
            </ButtonWithPromise>
          </>
        }
      />
    </>
  );
};

// ---------------------------------------------------------------------------
// Setup flow — three steps: pick object → pick list view → configure mappings
// ---------------------------------------------------------------------------

type Step = 'PICK_OBJECT' | 'PICK_LIST_VIEW' | 'CONFIGURE';

const ObjectPickerStep = ({
  orgId,
  initialObject,
  onCancel,
  onNext
}: {
  orgId: string;
  initialObject: string;
  onCancel: () => void;
  onNext: (objectName: string) => void;
}) => {
  const { t } = useUnsafeTranslation();
  const [objectName, setObjectName] = useState(initialObject);
  const [objects, loading] = useSalesforceObjects(orgId);
  const writable = (objects?.objects ?? []).filter((o) => o.createable || o.updateable);

  return (
    <>
      <DialogContent>
        <FormRow>
          <Img maxHeight={32} src={META.logoUrl} />
        </FormRow>
        <FormSection>
          <TextField
            select
            fullWidth
            label={t('Salesforce object')}
            value={objectName}
            disabled={loading}
            onChange={(e) => setObjectName(e.target.value)}
          >
            {writable.map((o) => (
              <MenuItem key={o.name} value={o.name}>
                {o.label} ({o.name})
              </MenuItem>
            ))}
          </TextField>
          <Typography variant="caption" color="textSecondary">
            {t(
              "Pick the Salesforce object you want to import users from. Contact is the typical choice."
            )}
          </Typography>
        </FormSection>
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onCancel}>
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!objectName}
          onClick={() => onNext(objectName)}
        >
          {t('Next')}
        </Button>
      </DialogActions>
    </>
  );
};

const ListViewPickerStep = ({
  orgId,
  objectName,
  onBack,
  onCancel,
  onNext
}: {
  orgId: string;
  objectName: string;
  onBack: () => void;
  onCancel: () => void;
  onNext: (listView: SalesforceListViewSummary) => void;
}) => {
  const { t } = useUnsafeTranslation();
  const [listViewId, setListViewId] = useState('');
  const [listViews, loading, error] = useSalesforceListViews(orgId, objectName);

  const options = listViews?.listviews ?? [];
  const selected = options.find((lv) => lv.id === listViewId);

  return (
    <>
      <DialogContent>
        <FormRow>
          <Img maxHeight={32} src={META.logoUrl} />
        </FormRow>
        {error && (
          <FormRow>
            <Alert severity="error">{t('Unable to load list views.')}</Alert>
          </FormRow>
        )}
        <FormSection>
          <Typography variant="caption" color="textSecondary">
            {t('Object: {{name}}', { name: objectName })}
          </Typography>
          <TextField
            select
            fullWidth
            label={t('List view')}
            value={listViewId}
            disabled={loading}
            onChange={(e) => setListViewId(e.target.value)}
            helperText={
              loading
                ? t('Loading list views from Salesforce...')
                : options.length === 0
                ? t('No SOQL-compatible list views are available on this object.')
                : t(
                    "Pick a saved list view. Its row filter and column set drive the import."
                  )
            }
          >
            {options.map((lv) => (
              <MenuItem key={lv.id} value={lv.id}>
                {lv.label}
              </MenuItem>
            ))}
          </TextField>
        </FormSection>
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onBack}>
          {t('Back')}
        </Button>
        <Button color="primary" onClick={onCancel}>
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!selected}
          onClick={() => selected && onNext(selected)}
        >
          {t('Next')}
        </Button>
      </DialogActions>
    </>
  );
};

const ConfigureStep = ({
  orgId,
  userId,
  objectName,
  listView,
  onBack,
  onCancel,
  customFields
}: {
  orgId: string;
  userId: string;
  objectName: string;
  listView: SalesforceListViewSummary;
  onBack: () => void;
  onCancel: () => void;
  customFields: CustomUserField[];
}) => {
  const { t } = useUnsafeTranslation();
  const userFields = useUserFields(customFields);

  const [detail, detailLoading] = useSalesforceListViewColumns(
    orgId,
    objectName,
    listView.id
  );
  const sampleRow = detail?.sampleRows?.[0];

  const [state, setState] = useState<Doc<UserImportDataSourceSalesforce>>({
    id: shortid(),
    collection: FS.userImportDataSources,
    data: {
      orgId,
      type: UserImportSourceType.SALESFORCE,
      status: UserImportDataSourceStatus.OK,
      detailedStatus: '',
      lastUpdate: null,
      createdBy: userId,
      createdAt: now(),
      objectName,
      listViewId: listView.id,
      listViewName: listView.label,
      name: `${objectName} — ${listView.label}`,
      customMappings: [emptyMapping()]
    }
  });

  return (
    <>
      <DialogContent>
        <FormRow>
          <Img maxHeight={32} src={META.logoUrl} />
        </FormRow>
        <FormRow>
          <AlertBox color="blue">
            {t(
              'Importing from "{{label}}" on {{object}}. Map the columns shown on the right to Dado user fields on the left.',
              { label: listView.label, object: objectName }
            )}
          </AlertBox>
        </FormRow>
        {detailLoading && !detail ? (
          <Loader height={150} />
        ) : (
          <>
            {sampleRow && (
              <FormRow>
                <Typography variant="caption" color="textSecondary">
                  {t('Sample row:')} {JSON.stringify(sampleRow)}
                </Typography>
              </FormRow>
            )}
            <CustomImportMultiMappingForm2
              additionalCompositeFields={['$FULL_NAME$']}
              fieldTerm={t('column')}
              integrationName="Salesforce"
              userFields={userFields}
              value={{
                main: state.data.customMappings,
                others: state.data.inferredOtherUsers?.map((v) => v.customMappings)
              }}
              onChange={(nextVal) =>
                setState((x) => ({
                  ...x,
                  data: {
                    ...x.data,
                    customMappings: nextVal.main,
                    inferredOtherUsers:
                      nextVal.others?.map((v) => ({ customMappings: v })) || []
                  }
                }))
              }
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onBack}>
          {t('Back')}
        </Button>
        <Button color="primary" onClick={onCancel}>
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={async () => {
            await setDoc(state);
            onCancel();
          }}
        >
          {t('Add import source')}
        </Button>
      </DialogActions>
    </>
  );
};

export const SalesforceSetupFlow = ({
  orgId,
  onClose,
  customFields
}: {
  orgId: string;
  onClose: () => void;
  customFields: CustomUserField[];
}) => {
  const { id: userId } = useCurrentUser();
  const [config, configLoading, configError] = useSalesforceConfig(orgId, userId);

  const [step, setStep] = useState<Step>('PICK_OBJECT');
  const [objectName, setObjectName] = useState<string>(DEFAULT_OBJECT);
  const [listView, setListView] = useState<SalesforceListViewSummary | null>(null);

  if (configLoading && !config) {
    return <Loader height={150} />;
  }
  if (configError) {
    return <SomethingWentWrong height={150} />;
  }

  const isConnected = !!(config?.data.refreshToken && config.data.connectedAt);
  if (!isConnected) {
    return <IntegrationNotConfigured integration="Salesforce" onClose={onClose} />;
  }

  if (step === 'PICK_OBJECT') {
    return (
      <ObjectPickerStep
        orgId={orgId}
        initialObject={objectName}
        onCancel={onClose}
        onNext={(name) => {
          setObjectName(name);
          setStep('PICK_LIST_VIEW');
        }}
      />
    );
  }
  if (step === 'PICK_LIST_VIEW') {
    return (
      <ListViewPickerStep
        orgId={orgId}
        objectName={objectName}
        onBack={() => setStep('PICK_OBJECT')}
        onCancel={onClose}
        onNext={(lv) => {
          setListView(lv);
          setStep('CONFIGURE');
        }}
      />
    );
  }
  if (step === 'CONFIGURE' && listView) {
    return (
      <ConfigureStep
        orgId={orgId}
        userId={userId}
        objectName={objectName}
        listView={listView}
        onBack={() => setStep('PICK_LIST_VIEW')}
        onCancel={onClose}
        customFields={customFields}
      />
    );
  }
  return null;
};

// ---------------------------------------------------------------------------
// List row renderer
// ---------------------------------------------------------------------------

export const ImportSourceRowSalesforce = ({
  d
}: {
  d: Doc<UserImportDataSourceSalesforce>;
}) => (
  <ImportSourceListItem
    enabled={d.data.status === UserImportDataSourceStatus.OK}
    logoUrl={META.smallLogoUrl}
    name={d.data.name || `${META.name} — ${d.data.listViewName}`}
  />
);
